import { useMemo } from 'react';
import { AllSubSteps } from './AllSubSteps';
import { useAssemblySteps } from './categoryBands';
import { TableCard } from './TableCard';
import { usePartsRun } from './partsRun';
import { madePieces } from './SubStepChips';
import { foldsOf, pieceIdByKey, pieceNames, useStepsRun, subStepLabel } from './stepsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// THE SUB-STEP TABLE'S INPUTS, over the whole manual — the steps phase's and the joint view's one
// way of making them, so the two tables name a gesture's pieces the same way.
//
// EVERY SUB-STEP THE MANUAL HAS, flattened in printed order — "1A", "1B", "2A". A step nobody has
// analysed contributes nothing rather than an empty place to stand. WHAT EACH GESTURE IS ALREADY
// HOLDING, built once over all of them (see madePieces), and THE PIECES BY ID, NAMED, for the
// sentences (see subStepText).
export function useSubStepTable(docId: string) {
  const { language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const st = useStepsRun(docId);
  // AND WHICH ROWS ARE A HARDWARE PREPARED IN THE HAND, played by the row that uses it (see
  // foldsOf): drawn there, not as rows of their own. Still listed, as `foldsInto`, because what a
  // gesture holds and leaves is walked over every row (see AllSubSteps' `all`).
  const subPlaces = useMemo(() => {
    const folds = foldsOf(steps, st.subSteps);
    const places = steps.flatMap((s) =>
      (st.subSteps[s.category] ?? []).map((sub) => ({
        key: `${s.category}#${sub.letter}`,
        label: subStepLabel(s.num, sub.letter),
        step: s,
        sub,
        foldsInto: folds.get(`${s.category}#${sub.letter}`) ?? null,
      })));
    return places.map((p) => ({
      ...p,
      preps: places.filter((q) => q.foldsInto === p.key).map((q) => q.sub),
    }));
  }, [steps, st.subSteps]);
  const pieceName = useMemo(() => pieceNames(steps, st.subSteps, language), [steps, st.subSteps, language]);
  const made = useMemo(
    () => madePieces(subPlaces, language, pieceIdByKey(steps, st.subSteps)),
    [subPlaces, language, steps, st.subSteps],
  );
  return { subPlaces, pieceName, made, items };
}

// ONE JOINT'S SUB-STEPS, as the steps phase tables them — under the joint's holes, in a titled card
// as they are.
export function JointSubSteps({ docId, keys }: { docId: string; keys: string[] }) {
  const { t } = useLanguage();
  const { subPlaces, pieceName, made, items } = useSubStepTable(docId);
  const rows = subPlaces.filter((p) => keys.includes(p.key));
  if (!rows.length) return null;
  return (
    <TableCard title={t('notice.steps.subSteps')}>
      <AllSubSteps pieces={pieceName} rows={rows} all={subPlaces} items={items} made={made} carded />
    </TableCard>
  );
}
